import React from "react";
import PropTypes from "prop-types";
import "./style.scss";
import line from "../../../assets/icons/line-stroke.svg";
import { FormatUSD } from "../../../utils/helpers/string";
import infoIcon from "../../../assets/icons/info.svg";
import { Tooltip } from "antd";

const getStatusClass = (result) => {
  if (!result) return "danger status-box";
  const status = result.toLowerCase();
  switch (status) {
    case "passed":
      return "pass status-box";
    case "in progress":
      return "inProgress status-box";
    default:
      return "danger status-box";
  }
};

const formatValue = (value, preText = "", postText = "") => {
  if (value === "N/A" || value === undefined || value === null) return "N/A";
  const prefix = typeof preText === "function" ? preText(value) : `${preText}${value}`;
  return `${prefix}${postText}`;
};

const ConsistencyTooltip = ({ item, lastData }) => (
  <div className="consistency_tooltip_box">
    <div className="tooltip_flex_row">
      <div className="upper_sec">
        <p>{FormatUSD(item?.best_day_pnl || 0)} (biggest day)</p>
        <hr className="tooltip_divider" />
        <p>{FormatUSD(item?.total_profit || 0)} (profit since payout request)</p>
      </div>
      <div className="percentage_display">
        <p> × 100 = {item?.percentage}% consistency</p>
      </div>
    </div>
    <br />
    <div className="consistency_tooltip_box_info">
      <p>
        Profit required to reach {item?.maximum_consistency}% consistency:{" "}
        <strong>
          {lastData?.remaining_consistency_profit
            ? FormatUSD(lastData.remaining_consistency_profit)
            : "N/A"}
        </strong>{" "}
        Consistency resets when a payout is Requested.
        <br />
        <span>If there is no profit overall your account will be N/A.</span>
      </p>
    </div>
  </div>
);

const GoalsBox = ({ item, newMaxLoss = [] }) => {
  const lastData = newMaxLoss?.[newMaxLoss.length - 1] || null;

  const renderProgressBar = () => {
    if (item.id === "max_loss") {
      return (
        <div className="sliderMaxValue">
          Do not let your account drop below this equity level
        </div>
      );
    }

    let percentage = 0;
    const parsedPercentage = parseFloat(item.percentage);
    if (!isNaN(parsedPercentage)) {
      percentage = Math.max(0, parsedPercentage);
    } else {
      const minMaxNum = parseFloat(item?.minMax);
      const targetNum = parseFloat(item?.target);
      if (!isNaN(minMaxNum) && !isNaN(targetNum) && targetNum !== 0) {
        percentage = (Math.abs(minMaxNum) / Math.abs(targetNum)) * 100;
      }
    }

    const status = item.result?.toLowerCase();
    const statusClass = status === "passed" ? "Passed" : status === "in progress" ? "in_Progress" : status === "failed" ? "danger" : "";

    return (
      <div className="sliderBase">
        <div style={{ width: `${percentage}%` }} className={`sliderActive ${statusClass}`}>
          <div className="sliderCircle">
            <div className="sliderText">
              {formatValue(item.minMax === "N/A" ? 0 : item.minMax, item.preText, item.postText)}
            </div>
            <div className="sliderCircleInner" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="goals-box">
      <div className="goals_container">
        <div className="goals-box-content">
          <h3 className={item.id === "consistency" ? "consistency_heading" : ""}>
            {item.heading}{" "}
            {item.id === "consistency" && (
              <div style={{ position: "relative", display: "inline-block" }}>
                <Tooltip
                  title={<ConsistencyTooltip item={item} lastData={lastData} />}
                  placement="leftBottom"
                  overlayInnerStyle={{ width: 400, padding: 20 }}
                  color="#131313"
                >
                  <img src={infoIcon} alt="info" className="info_icon" />
                </Tooltip>
              </div>
            )}
          </h3>
          <div className="result_section">
            <p className="result">
              Result: {formatValue(item.minMax, item.preText, item.postText)}
            </p>
            {item.id === "consistency" && item.RequiredProfit && lastData && (
              <p className="result margintop10px">
                <img src={line} alt="line" />
                Required Profit (Updated at EoD):{" "}
                {formatValue(lastData.remaining_consistency_profit, FormatUSD)}
              </p>
            )}
          </div>
        </div>
        {!item.hideStatusIndicator && (
          <div className="goals-box-header">
            <div className={getStatusClass(item.result)}>
              <p>{item.result}</p>
            </div>
          </div>
        )}
      </div>
      <div className="slider-container">
        <div
          className="sliderMaxValue"
          style={{ fontSize: item.id === "max_loss" ? "20px" : undefined }}
        >
          {formatValue(item.target, item.preText, item.postText)}
        </div>
        {renderProgressBar()}
      </div>
    </div>
  );
};

function Goals({ data, newMaxLoss }) {
  const metrics = data?.account_metrics?.program_objectives;
  const calculated = data?.account_metrics?.calculated_data || {};
  const { phase, plan_name } = calculated;

  const goalsList = [
    {
      id: "profit",
      heading: "Profit",
      result: metrics?.profit_status || "N/A",
      minMax: metrics?.profit?.toFixed(2) || "N/A",
      target: metrics?.profit_target?.toFixed(2) || "N/A",
      preText: FormatUSD,
      percentage: metrics?.profit_progress_bar,
    },
    {
      id: "max_loss",
      heading: "Maximum Loss Limit",
      result: metrics?.max_loss_status || "N/A",
      minMax: metrics?.max_loss?.toFixed(2) || "N/A",
      target: metrics?.max_loss_target?.toFixed(2) || "N/A",
      preText: FormatUSD,
      percentage: metrics?.max_loss_progress_bar,
    },
    {
      id: "daily_loss",
      heading: "Daily loss",
      result: metrics?.max_daily_loss_status || "N/A",
      minMax: metrics?.max_daily_loss?.toFixed(2) || "N/A",
      target: metrics?.max_daily_loss_target?.toFixed(2) || "N/A",
      percentage: metrics?.daily_loss_progress_bar,
      preText: FormatUSD,
      phase,
      plan_name,
    },
    {
      id: "consistency",
      heading: `Best day cannot be greater than ${metrics?.maximum_consistency}% of your total profits (Consistency Target)`,
      result: calculated.consistency > metrics?.maximum_consistency ? "failed" : "",
      minMax: calculated.consistency > 0 ? calculated.consistency.toFixed(2) : "N/A",
      target: Math.max(100, calculated.consistency || 0).toFixed(2),
      percentage: calculated.consistency,
      postText: "%",
      RequiredProfit: "true",
      hideStatusIndicator: true,
      phase,
      plan_name,
      best_day_pnl: calculated.best_day_pnl,
      total_profit: calculated.total_profit,
      maximum_consistency: metrics?.maximum_consistency,
    },
  ];

  const filteredGoals = goalsList.filter((goal) => {
    if (goal.id === "daily_loss") {
      const restrictedPlansEval = ["Advanced Plan", "Standard Plan", "Premium Plan"];
      const restrictedPlansQual = ["Advanced Plan", "Premium Plan"];
      
      if (phase === "evaluation" && restrictedPlansEval.includes(plan_name)) return false;
      if (phase === "qualified" && restrictedPlansQual.includes(plan_name)) return false;
    }

    if (goal.id === "consistency" && phase === "evaluation" && plan_name === "Zero Plan") {
      return false;
    }

    return true;
  });

  return (
    <div className="goals box">
      <h2 className="component-heading-goals">Trading Objective</h2>
      <div className="goals-box-wrapper">
        {filteredGoals.map((item) => (
          <GoalsBox key={item.id} item={item} newMaxLoss={newMaxLoss} />
        ))}
      </div>
    </div>
  );
}

Goals.propTypes = {
  data: PropTypes.shape({
    account_metrics: PropTypes.shape({
      program_objectives: PropTypes.object,
      calculated_data: PropTypes.object,
    }),
  }).isRequired,
  newMaxLoss: PropTypes.array,
};

export default Goals;
